import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { orcamentosApi, categoriasApi, compartilhamentosApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, KpiCard, ProgressBar } from '../../components/ui/Card'
import { Badge, DonoBadge } from '../../components/ui/Badge'
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { MonthNavigator } from '../../components/ui/Navigation'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { StatusCompartilhamento } from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import { PlanejamentoPanel, Dica } from './PlanejamentoPanel'
import type { Orcamento, ConviteOrcamento, SugestaoLimite, LancamentosDoOrcamento, PlanejamentoTransferencia } from '../../types'

const TIPOS_META = [
  { valor: 1, label: 'Teto', dica: 'Um limite máximo: você tenta gastar até esse valor.' },
  { valor: 2, label: 'Valor fixo', dica: 'Uma conta de valor certo, como aluguel ou mensalidade: o ideal é gastar exatamente isso.' },
  { valor: 3, label: 'Mínimo', dica: 'Um valor que você quer guardar ou gastar pelo menos, como uma reserva.' },
]
const CADENCIAS = [
  { valor: 1, label: 'Por semana' },
  { valor: 2, label: 'Por dia' },
  { valor: 3, label: 'A cada X dias' },
]
const SITUACAO_TONE = { verde: 'income', ambar: 'warning', vermelho: 'expense', neutro: 'info' } as const
const PROJETAR_COMO = [
  { valor: 1, label: 'Pelo histórico', dica: 'Divide o que falta gastar como nos últimos meses: cada cartão na fatura certa e cada conta no seu saldo.' },
  { valor: 2, label: 'Crédito', dica: 'O que falta gastar vai para as próximas faturas, nos cartões que você mais usa nesta categoria.' },
  { valor: 3, label: 'À vista', dica: 'O que falta gastar sai na hora, das contas de onde costuma sair (inclusive vale-alimentação).' },
]

/** O que compõe o valor gasto do orçamento: os mesmos lançamentos que o Orçamento soma. */
function LancamentosOrcamentoModal({ orcamento, onClose, onEditar }: { orcamento: Orcamento; onClose: () => void; onEditar: () => void }) {
  const { data: d, isLoading, isError } = useQuery({ queryKey: ['orcamento-lancamentos', orcamento.id], queryFn: () => orcamentosApi.lancamentos(orcamento.id) })
  const dados: LancamentosDoOrcamento | undefined = d

  return (
    <Modal
      open onClose={onClose} title={`${orcamento.categoriaNome} · lançamentos do mês`} size="md"
      footer={<><Button variant="light" onClick={onClose}>Fechar</Button><Button icon="ri-pencil-fill" onClick={onEditar}>{orcamento.souDono ? 'Editar limite e categoria' : 'Abrir ajustes'}</Button></>}
    >
      {isLoading ? <LoadingState /> : isError || !dados ? <p className="text-13 text-expense">Não consegui carregar os lançamentos.</p> : (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between border-b border-border/60 pb-2">
            <span className="text-13 text-muted">{orcamento.conjunto ? 'Seus lançamentos' : 'Soma dos lançamentos'}</span>
            <span className="text-lg font-semibold text-white">{formatMoney(dados.meuTotal)}</span>
          </div>
          {dados.outrosParticipantes > 0 && <p className="text-13 text-muted">Mais {formatMoney(dados.outrosParticipantes)} de outras pessoas do orçamento conjunto (sem detalhe).</p>}
          {dados.aviso && <p className="text-13 text-muted">{dados.aviso}</p>}
          {dados.itens.length === 0 && !dados.aviso ? (
            <>
              <p className="text-13 text-muted">Nenhum lançamento na categoria {orcamento.categoriaNome} neste mês.</p>
              <p className="text-13 text-warning">Só entram lançamentos desta categoria. Se o gasto foi lançado em outra (por exemplo Casa), troque a categoria do lançamento ou a deste orçamento.</p>
            </>
          ) : (
            <ul className="divide-y divide-border/60">
              {dados.itens.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-white">{l.descricao}</p>
                    <p className="text-2xs text-muted">{formatDate(l.data)}{l.origem ? ` · ${l.origem}` : ''}{l.pendente ? ' · na fatura' : ''}</p>
                  </div>
                  <span className="whitespace-nowrap font-semibold text-expense">{formatMoney(l.valor)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  )
}

function PainelSugestao({ s, onUsar }: { s: SugestaoLimite; onUsar: (v: number) => void }) {
  if (s.sugerido == null) return <p className="text-13 text-muted">{s.aviso}</p>
  const corMes = s.mesAberto?.status === 'acima' ? 'text-expense' : s.mesAberto?.status === 'abaixo' ? 'text-income' : 'text-muted'
  return (
    <div className="space-y-2 rounded border border-primary/60 p-3">
      <p className="text-2xs text-muted">
        {s.metodo === 'media' ? 'Média' : 'Mediana'} do gasto real dos últimos {s.mesesAnalisados} meses fechados ({s.mesesComGasto} com gasto). Nada é salvo até você clicar em Salvar.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="light" onClick={() => onUsar(s.sugerido!)}>Apertado {formatMoney(s.sugerido)}</Button>
        {s.folgado != null && <Button size="sm" variant="light" onClick={() => onUsar(s.folgado!)}>Folgado {formatMoney(s.folgado)}</Button>}
      </div>
      <p className="text-2xs text-muted">Apertado é o gasto típico; Folgado cobre os meses mais caros (percentil 75).</p>
      {s.sublimites.length > 0 && (
        <div>
          <p className="text-13 font-medium text-white">Por grupo de itens (notas cobrem {Math.round((s.cobertura ?? 0) * 100)}% do gasto)</p>
          {s.sublimites.map((x) => <p key={x.grupo} className="text-2xs text-muted">{x.nome}: {formatMoney(x.valor)}</p>)}
        </div>
      )}
      {s.aviso && <p className="text-2xs text-muted">{s.aviso}</p>}
      {s.mesAberto && (
        <p className={`text-2xs ${corMes}`}>
          Este mês: gasto {formatMoney(s.mesAberto.gastoAteHoje)} até hoje; nos meses anteriores, até esta data já se gastava cerca de {formatMoney(s.mesAberto.esperadoAteHoje)} ({s.mesAberto.status === 'acima' ? 'acima do ritmo habitual' : s.mesAberto.status === 'abaixo' ? 'abaixo do ritmo habitual' : 'no ritmo habitual'}).
        </p>
      )}
    </div>
  )
}

export function OrcamentoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const now = new Date()
  const [mes, setMes] = useState(now.getMonth() + 1)
  const [ano, setAno] = useState(now.getFullYear())
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    categoriaId: '', limiteValor: '', alertaAtivo: true, conjunto: false, participantesUserIds: [] as string[],
    repeticao: 'mensal' as 'mensal' | 'intervalo' | 'unico', intervaloMeses: '2', somenteEsteMes: false,
    acumularSaldo: false, modoAlvo: false, valorAlvo: '', dataAlvo: '',
    tipoMeta: 1, essencial: false, ritmoAtivo: false, ritmoCadencia: 1, ritmoDias: '3', ritmoValor: '', ritmoAcumula: false, projetarComo: 1,
  })
  const [editando, setEditando] = useState<Orcamento | null>(null)
  const [lancamentosDe, setLancamentosDe] = useState<Orcamento | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [paraExcluir, setParaExcluir] = useState<Orcamento | null>(null)
  const [paraSair, setParaSair] = useState<Orcamento | null>(null)
  const [convite, setConvite] = useState<ConviteOrcamento | null>(null)
  const [categoriaConvite, setCategoriaConvite] = useState('')
  const [historicoOpen, setHistoricoOpen] = useState(false)
  const [escopo, setEscopo] = useState<'eu' | 'casal'>('eu')
  const [desfeitos, setDesfeitos] = useState<PlanejamentoTransferencia[]>([])
  const [parOcupado, setParOcupado] = useState<string | null>(null)

  const { data: resumo, isLoading, isError } = useQuery({
    queryKey: ['orcamento', mes, ano, escopo],
    queryFn: () => orcamentosApi.getResumo(mes, ano, escopo),
  })
  const plano = resumo?.planejamento
  const temPlano = !!plano
  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
    enabled: modalOpen || !!convite,
  })
  const { data: pessoas = [] } = useQuery({
    queryKey: ['pessoas-relacionadas'],
    queryFn: compartilhamentosApi.relacionadas,
    enabled: modalOpen,
  })
  const { data: convites = [] } = useQuery({
    queryKey: ['orcamento-convites'],
    queryFn: orcamentosApi.listarConvites,
  })

  const salvar = useMutation({
    mutationFn: async () => {
      // trocar a categoria de um orçamento que já existe: antes de salvar, para não criar um orçamento novo
      if (editando && editando.souDono && form.categoriaId && form.categoriaId !== editando.categoriaId)
        await orcamentosApi.alterarCategoria(editando.id, form.categoriaId)
      return orcamentosApi.salvar({
      categoriaId: form.categoriaId, mes, ano,
      limiteValor: form.modoAlvo ? null : Number(form.limiteValor), alertaAtivo: form.alertaAtivo,
      conjunto: form.conjunto, participantesUserIds: form.conjunto ? form.participantesUserIds : undefined,
      repetirACadaMeses: form.repeticao === 'unico' ? null : form.repeticao === 'mensal' ? 1 : Number(form.intervaloMeses),
      somenteEsteMes: form.somenteEsteMes,
      acumularSaldo: form.acumularSaldo || form.modoAlvo,
      valorAlvo: form.modoAlvo ? Number(form.valorAlvo) : null,
      dataAlvo: form.modoAlvo ? form.dataAlvo : null,
      ...(temPlano ? {
        tipoMeta: form.tipoMeta, essencial: form.essencial,
        ritmoAtivo: form.ritmoAtivo,
        ritmoCadencia: form.ritmoAtivo ? form.ritmoCadencia : null,
        ritmoDias: form.ritmoAtivo && form.ritmoCadencia === 3 ? Number(form.ritmoDias) : null,
        ritmoValor: form.ritmoAtivo ? Number(form.ritmoValor) : null,
        ritmoAcumula: form.ritmoAtivo && form.ritmoAcumula,
        projetarComo: form.projetarComo,
      } : {}),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      qc.invalidateQueries({ queryKey: ['onboarding'] })
      toast.success('Orçamento salvo.')
      setModalOpen(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const alternarAlerta = useMutation({
    mutationFn: (o: Orcamento) => orcamentosApi.salvar({
      categoriaId: o.categoriaId, mes, ano, limiteValor: o.valorAlvo ? null : o.limiteValor, alertaAtivo: !o.alertaAtivo,
      conjunto: o.conjunto, participantesUserIds: o.participantes?.map((p) => p.userId),
      repetirACadaMeses: o.repetirACadaMeses ?? null,
      somenteEsteMes: o.aplicarSomenteEsteMes,
      acumularSaldo: o.acumularSaldo, valorAlvo: o.valorAlvo ?? null, dataAlvo: o.dataAlvo ?? null,
    }),
    onSuccess: (_, o) => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success(o.alertaAtivo ? 'Alerta desativado.' : 'Alerta ativado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const parear = useMutation({
    mutationFn: async ({ t, desfazer }: { t: PlanejamentoTransferencia; desfazer: boolean }) => {
      setParOcupado(t.id)
      if (desfazer) await orcamentosApi.desfazerPareamento(t.id)
      else await orcamentosApi.refazerPareamento(t.id)
    },
    onSuccess: (_, { t, desfazer }) => {
      setDesfeitos((l) => desfazer ? [...l.filter((x) => x.id !== t.id), t] : l.filter((x) => x.id !== t.id))
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success(desfazer ? 'Desfeito: os dois lançamentos voltam a contar como renda e gasto.' : 'Refeito: voltam a contar como transferência entre vocês.')
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setParOcupado(null),
  })

  const remover = useMutation({
    mutationFn: (id: string) => orcamentosApi.remover(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success('Orçamento removido.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const sair = useMutation({
    mutationFn: (participanteId: string) => orcamentosApi.sairDoConjunto(participanteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success('Você saiu do orçamento conjunto.')
      setParaSair(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const alternarMinhaNotificacao = useMutation({
    mutationFn: ({ participanteId, ativas }: { participanteId: string; ativas: boolean }) =>
      orcamentosApi.atualizarNotificacaoParticipante(participanteId, ativas),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orcamento'] }) },
    onError: (e: Error) => toast.error(e.message),
  })

  const aceitarConvite = useMutation({
    mutationFn: () => orcamentosApi.aceitarConvite(convite!.participanteId, categoriaConvite),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      qc.invalidateQueries({ queryKey: ['orcamento-convites'] })
      toast.success('Convite aceito — seu gasto já entra na soma.')
      setConvite(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const recusarConvite = useMutation({
    mutationFn: (participanteId: string) => orcamentosApi.recusarConvite(participanteId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orcamento-convites'] }); toast.success('Convite recusado.') },
    onError: (e: Error) => toast.error(e.message),
  })

  const [sugestao, setSugestao] = useState<SugestaoLimite | null>(null)
  const sugerir = useMutation({ mutationFn: () => orcamentosApi.sugestaoLimite(form.categoriaId), onSuccess: setSugestao, onError: (e: Error) => toast.error(e.message) })

  function openModal(o?: Orcamento, categoriaId?: string) {
    setSugestao(null)
    const repeticao = !o || o.repetirACadaMeses === 1 ? 'mensal' : o.repetirACadaMeses ? 'intervalo' : 'unico'
    const modoAlvo = !!o?.valorAlvo
    setForm({
      categoriaId: o?.categoriaId ?? categoriaId ?? '', limiteValor: o ? String(o.limiteValor) : '', alertaAtivo: o?.alertaAtivo ?? true,
      conjunto: o?.conjunto ?? false, participantesUserIds: o?.participantes?.map((p) => p.userId) ?? [],
      repeticao, intervaloMeses: o?.repetirACadaMeses && o.repetirACadaMeses > 1 ? String(o.repetirACadaMeses) : '2',
      somenteEsteMes: false,
      acumularSaldo: o?.acumularSaldo ?? false, modoAlvo,
      valorAlvo: o?.valorAlvo ? String(o.valorAlvo) : '', dataAlvo: o?.dataAlvo ? o.dataAlvo.slice(0, 10) : '',
      tipoMeta: o?.tipoMeta ?? 1, essencial: o?.essencial ?? false, ritmoAtivo: o?.ritmoAtivo ?? false,
      ritmoCadencia: o?.ritmoCadencia ?? 1, ritmoDias: o?.ritmoDias ? String(o.ritmoDias) : '3',
      ritmoValor: o?.ritmoValor ? String(o.ritmoValor) : '', ritmoAcumula: o?.ritmoAcumula ?? false,
      projetarComo: o?.projetarComo ?? 1,
    })
    setEditando(o ?? null)
    setErro(null)
    setModalOpen(true)
  }

  function submit() {
    if (!form.categoriaId) {
      setErro('Escolha a categoria.')
      return
    }
    if (form.modoAlvo ? (!form.valorAlvo || !form.dataAlvo) : !form.limiteValor) {
      setErro(form.modoAlvo ? 'Informe o valor alvo e a data.' : 'Informe o limite mensal.')
      return
    }
    if (temPlano && form.ritmoAtivo) {
      if (!(Number(form.ritmoValor) > 0)) { setErro('Informe quanto pode gastar por período no ritmo.'); return }
      if (form.ritmoCadencia === 3 && !(Number(form.ritmoDias) >= 1)) { setErro('Informe de quantos em quantos dias é o ritmo.'); return }
    }
    salvar.mutate()
  }

  function togglePessoa(userId: string) {
    setForm((f) => ({
      ...f,
      participantesUserIds: f.participantesUserIds.includes(userId)
        ? f.participantesUserIds.filter((id) => id !== userId)
        : [...f.participantesUserIds, userId],
    }))
  }

  function abrirConvite(c: ConviteOrcamento) {
    setConvite(c)
    setCategoriaConvite('')
  }

  const cats = resumo?.categorias ?? []
  const noMes = new Map((plano?.planoDoMes?.gastoEPrevisto.nosOrcamentos ?? []).map((x) => [x.orcamentoId, x]))

  return (
    <>
      <PageHeader title="Orçamento" />

      {convites.length > 0 && (
        <Card className="mb-6 border border-primary/30">
          <CardHeader title="Convites de orçamento conjunto" icon="ri-team-line" />
          <CardBody className="space-y-3">
            {convites.map((c) => (
              <div key={c.participanteId} className="flex flex-wrap items-center justify-between gap-3 rounded bg-surface2 p-3">
                <div>
                  <p className="text-13 text-white">
                    <strong>{c.donoNome}</strong> te convidou pra juntar sua categoria com <strong>{c.categoriaNomeSugerida}</strong>{' '}
                    ({formatMoney(c.limiteValor)}/mês)
                  </p>
                  <p className="mt-0.5 text-2xs text-muted">
                    {c.outrosParticipantesNomes.length > 0
                      ? `Também no grupo: ${c.outrosParticipantesNomes.join(', ')}`
                      : 'Você seria a primeira pessoa a entrar.'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="light" onClick={() => recusarConvite.mutate(c.participanteId)} loading={recusarConvite.isPending}>Recusar</Button>
                  <Button onClick={() => abrirConvite(c)}>Aceitar</Button>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <MonthNavigator mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); setDesfeitos([]) }} />
          {temPlano && (
            <div className="inline-flex rounded border border-border/70 p-0.5" role="group" aria-label="Quem entra na conta">
              {([{ v: 'eu' as const, l: 'Só eu' }, { v: 'casal' as const, l: 'Eu + pessoa' }]).map((op) => (
                <button
                  key={op.v} type="button" aria-pressed={escopo === op.v}
                  onClick={() => { setEscopo(op.v); setDesfeitos([]) }}
                  className={`h-8 rounded px-3 text-13 transition-colors ${escopo === op.v ? 'bg-primary/10 text-primary' : 'text-muted hover:text-white'}`}
                >
                  {op.l}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="soft-info" icon="ri-line-chart-line" onClick={() => setHistoricoOpen(true)}>Ver histórico</Button>
          <Button data-tutorial="orcamento.novo" icon="ri-add-line" onClick={() => openModal()}>Definir orçamento</Button>
        </div>
      </div>

      <HistoricoOrcamentoModal open={historicoOpen} onClose={() => setHistoricoOpen(false)} />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message="Erro ao carregar o orçamento." />
      ) : (
        <div className="space-y-6">
          {plano ? (
            <>
              {escopo === 'casal' && !plano.casalAtivo && (
                <p className="rounded bg-surface2 p-3 text-13 text-muted">Ninguém compartilha Orçamento e Lançamentos com você, então a conta é só sua.</p>
              )}
              <PlanejamentoPanel
                p={plano} mes={mes} ano={ano}
                onCriarOrcamento={(categoriaId) => openModal(undefined, categoriaId)}
                parOcupado={parOcupado} desfeitos={desfeitos}
                onDesfazerPar={(id) => { const t = plano.transferenciasEntreVoces.find((x) => x.id === id); if (t) parear.mutate({ t, desfazer: true }) }}
                onRefazerPar={(id) => { const t = desfeitos.find((x) => x.id === id); if (t) parear.mutate({ t, desfazer: false }) }}
              />
            </>
          ) : (
          <>
          <div className="grid gap-6 sm:grid-cols-3">
            <KpiCard title="Renda prevista" value={formatMoney(resumo?.rendaPrevista ?? 0)} icon="ri-arrow-left-down-line" tone="income" />
            <KpiCard title="Despesas fixas previstas" value={formatMoney(resumo?.despesasFixasPrevistas ?? 0)} icon="ri-repeat-line" tone="expense" />
            <KpiCard
              title="Renda disponível"
              value={formatMoney(resumo?.rendaDisponivel ?? 0)}
              icon="ri-hand-coin-line"
              tone={(resumo?.rendaDisponivel ?? 0) >= 0 ? 'income' : 'expense'}
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-3">
            <KpiCard title="Total orçado" value={formatMoney(resumo?.totalOrcado ?? 0)} icon="ri-wallet-line" tone="info" />
            <KpiCard title="Total gasto" value={formatMoney(resumo?.totalGasto ?? 0)} icon="ri-shopping-cart-2-line" tone="expense" />
            <KpiCard
              title="Sobra final"
              value={formatMoney(resumo?.sobraFinal ?? 0)}
              icon="ri-scales-3-line"
              tone={(resumo?.sobraFinal ?? 0) >= 0 ? 'income' : 'expense'}
              change={resumo && resumo.totalOrcado > 0 ? { value: `${resumo.percentualGeral.toFixed(0)}% do orçado já usado`, positive: resumo.percentualGeral < 80 } : undefined}
            />
          </div>

          {resumo?.semCategoria && resumo.semCategoria.quantidade > 0 && (
            <div className="rounded border border-warning/60 bg-warning/5 p-3 text-13">
              <p className="font-medium text-warning">Gastos sem categoria ficam fora dos limites</p>
              <p className="text-muted">
                {resumo.semCategoria.quantidade} lançamento(s), {formatMoney(resumo.semCategoria.total)} neste mês, não entram em nenhum orçamento. Em Lançamentos, filtre por Categoria › Sem categoria e escolha a categoria de cada um.{' '}
                <Link to="/transacoes" className="font-medium text-primary hover:underline">Ver lançamentos</Link>
              </p>
            </div>
          )}

          {resumo && (
            <p className={`text-13 font-medium ${resumo.sobraFinal >= 0 ? 'text-income' : 'text-expense'}`}>
              {resumo.sobraFinal >= 0
                ? `Renda disponível ${formatMoney(resumo.rendaDisponivel)} − total orçado ${formatMoney(resumo.totalOrcado)}: o planejamento cabe, sobram ${formatMoney(resumo.sobraFinal)}.`
                : `Renda disponível ${formatMoney(resumo.rendaDisponivel)} − total orçado ${formatMoney(resumo.totalOrcado)}: o planejamento estoura em ${formatMoney(Math.abs(resumo.sobraFinal))} — ajuste algum orçamento.`}
            </p>
          )}

          {resumo?.reservasEmRisco && resumo.reservasEmRisco.length > 0 && (
            <Card className="border border-warning/30">
              <CardBody className="space-y-2">
                <p className="flex items-center gap-1.5 text-13 font-medium text-warning">
                  <i className="ri-error-warning-line" aria-hidden /> Sua renda não cobriu tudo que você orçou este mês — essas reservas podem ter ficado sem o aporte completo:
                </p>
                <ul className="space-y-1 text-13 text-muted">
                  {resumo.reservasEmRisco.map((r) => (
                    <li key={r.categoriaNome}>{r.categoriaNome} — aporte esperado: {formatMoney(r.aporteEsperado)}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

          </>
          )}

          <Card>
            <CardHeader title="Orçamento por categoria" />
            {resumo && cats.length > 0 && (
              <div className="border-b border-border/60 px-4 py-3">
                <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2 text-13">
                  <span className="text-muted">Orçado no mês <span className="text-base font-semibold text-white">{formatMoney(resumo.totalOrcado)}</span></span>
                  <span className="text-muted">
                    gasto <span className="font-medium text-white">{formatMoney(resumo.totalGasto)}</span> ({resumo.percentualGeral.toFixed(0)}%)
                    {' · '}falta <span className="font-medium text-white">{formatMoney(plano?.planoDoMes?.aindaAGastarNosOrcamentos ?? Math.max(0, resumo.totalSaldo))}</span>
                  </span>
                </div>
                <ProgressBar value={resumo.percentualGeral} tone={resumo.percentualGeral > 100 ? 'expense' : resumo.percentualGeral >= 80 ? 'warning' : 'income'} />
              </div>
            )}
            {cats.length === 0 ? (
              <EmptyState icon="ri-pie-chart-2-line" message="Nenhum orçamento definido para este mês." action={<Button variant="soft-primary" icon="ri-add-line" onClick={() => openModal()}>Definir orçamento</Button>} />
            ) : (
              <CardBody className="divide-y divide-border/60 p-0">
                {cats.map((o) => {
                  const tipo = o.tipoMeta ?? 1
                  const tone = o.situacao ? SITUACAO_TONE[o.situacao] : o.percentual > 100 ? 'expense' : o.percentual >= 80 ? 'warning' : 'income'
                  const estourou = tipo === 1 && o.percentual > 100
                  const minhasNotificacoes = o.souDono ? o.alertaAtivo : o.minhasNotificacoesAtivas
                  const meuParticipante = o.participantes?.find((p) => p.souEu)
                  return (
                    <div key={o.id} className="flex flex-wrap items-center gap-4 px-4 py-4">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-surface2 text-xl" style={o.categoriaCor ? { background: `${o.categoriaCor}22` } : undefined}>
                        {o.categoriaIcone ?? '📂'}
                      </span>
                      <div className="min-w-[200px] flex-1">
                        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                          <p className="flex items-center gap-2 font-medium text-white">
                            {o.categoriaNome}
                            <DonoBadge nome={o.ownerNome} />
                            {o.conjunto && <Badge variant="info"><i className="ri-team-line" aria-hidden /> Conjunto</Badge>}
                            {o.repetirACadaMeses === 1 && <Badge variant="muted"><i className="ri-repeat-line" aria-hidden /> Mensal</Badge>}
                            {o.repetirACadaMeses != null && o.repetirACadaMeses > 1 && <Badge variant="muted"><i className="ri-repeat-line" aria-hidden /> A cada {o.repetirACadaMeses} meses</Badge>}
                            {o.aplicarSomenteEsteMes && <Badge variant="muted"><i className="ri-flashlight-line" aria-hidden /> Ajuste pontual</Badge>}
                            {o.acumularSaldo && <Badge variant="info"><i className="ri-safe-2-line" aria-hidden /> {o.valorAlvo ? 'Meta' : 'Reserva'}</Badge>}
                            {o.alertaAtivado && <Badge variant={tone === 'expense' ? 'expense' : 'warning'}><i className="ri-alarm-warning-line" aria-hidden /> {o.percentual > 100 ? 'Excedido' : 'Atenção'}</Badge>}
                          </p>
                          <p className="text-13 text-muted">
                            <span className="font-medium text-white">{formatMoney(o.gastoAtual)}</span> de {formatMoney(o.limiteValor)}
                          </p>
                        </div>
                        <ProgressBar value={o.percentual} tone={tone} />
                        <p className="mt-1 flex justify-between gap-2 text-2xs text-muted">
                          <span className={estourou ? 'font-semibold text-expense' : ''}>
                            {o.percentual.toFixed(0)}% usado{estourou ? ' (passou do limite)' : ''}{o.conjunto && o.minhaParte != null ? ` · sua parte: ${formatMoney(o.minhaParte)}` : ''}
                          </span>
                          <span className={o.saldo < 0 && tipo !== 3 ? 'font-semibold text-expense' : ''}>
                            {tipo === 3
                              ? (o.saldo > 0 ? `Faltam ${formatMoney(o.saldo)} pro mínimo` : `Mínimo atingido (+${formatMoney(Math.abs(o.saldo))})`)
                              : o.saldo >= 0 ? `Restam ${formatMoney(o.saldo)}` : `Excedeu ${formatMoney(Math.abs(o.saldo))}`}
                          </span>
                        </p>
                        {(o.tipoMeta != null || o.podeGastarPorDia != null) && (
                          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted">
                            <span>{TIPOS_META.find((t) => t.valor === tipo)?.label}{o.essencial ? ' · essencial' : ''}</span>
                            {noMes.get(o.id) && <span>projetar: {PROJETAR_COMO.find((t) => t.valor === (o.projetarComo ?? 1))?.label.toLowerCase()}</span>}
                            {(noMes.get(o.id)?.percentualNoCartao ?? 0) > 0 && (
                              <span className="rounded bg-info/15 px-1.5 py-0.5 font-medium text-info">{noMes.get(o.id)!.percentualNoCartao.toFixed(0)}% no cartão</span>
                            )}
                            {o.podeGastarPorDia != null && <span>Pode gastar {formatMoney(o.podeGastarPorDia)} por dia</span>}
                          </p>
                        )}
                        {noMes.get(o.id)?.avisoProjecao && <p className="mt-1 text-2xs text-warning">{noMes.get(o.id)!.avisoProjecao}</p>}
                        {o.ritmoAtivo && o.ritmo && (
                          <div className={`mt-2 rounded px-2.5 py-1.5 text-2xs ${o.ritmo.adiantadoOuAtrasado >= 0 ? 'bg-income/10 text-income' : 'bg-expense/10 text-expense'}`}>
                            <p className="font-medium">
                              Ritmo ({formatDate(o.ritmo.periodoAtualInicio)} a {formatDate(o.ritmo.periodoAtualFim)}): gastou {formatMoney(o.ritmo.gastoNoPeriodo)} de {formatMoney(o.ritmo.valorDoPeriodo)}
                            </p>
                            <p>
                              {o.ritmo.adiantadoOuAtrasado >= 0
                                ? `Folga de ${formatMoney(o.ritmo.adiantadoOuAtrasado)}: você está abaixo do ritmo.`
                                : `${formatMoney(Math.abs(o.ritmo.adiantadoOuAtrasado))} acima do ritmo neste período.`}
                              {o.ritmoAcumula ? ' A sobra de cada período passa para o próximo.' : ''}
                            </p>
                          </div>
                        )}
                        {o.acumularSaldo && (
                          <p className="mt-1 text-2xs text-info">
                            Acumulado: {formatMoney(o.saldoAcumulado)}
                            {o.valorAlvo && o.dataAlvo && <> · meta {formatMoney(o.valorAlvo)} até {new Date(o.dataAlvo).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' })}</>}
                          </p>
                        )}
                        {o.conjunto && o.participantes && o.participantes.length > 0 && (
                          <p className="mt-1.5 flex flex-wrap gap-1.5">
                            {o.participantes.map((p) => (
                              <span key={p.id} className="inline-flex items-center gap-1 rounded bg-surface3 px-1.5 py-0.5 text-2xs text-muted">
                                {p.nome.split(' ')[0]}
                                {p.status === StatusCompartilhamento.Pendente && ' (pendente)'}
                              </span>
                            ))}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center">
                        <IconButton icon="ri-list-check-2" label={`Ver lançamentos de ${o.categoriaNome}`} tone="info" onClick={() => setLancamentosDe(o)} />
                        {o.souDono ? (
                          <>
                            <IconButton
                              icon={o.alertaAtivo ? 'ri-notification-3-line' : 'ri-notification-off-line'}
                              label={o.alertaAtivo ? `Desativar alerta de ${o.categoriaNome}` : `Ativar alerta de ${o.categoriaNome}`}
                              tone={o.alertaAtivo ? 'primary' : 'muted'}
                              disabled={alternarAlerta.isPending}
                              onClick={() => alternarAlerta.mutate(o)}
                            />
                            <IconButton icon="ri-pencil-fill" label={`Editar orçamento de ${o.categoriaNome}`} tone="info" onClick={() => openModal(o)} />
                            <IconButton icon="ri-delete-bin-5-fill" label={`Remover orçamento de ${o.categoriaNome}`} tone="danger" onClick={() => setParaExcluir(o)} />
                          </>
                        ) : (
                          <>
                            <IconButton
                              icon={minhasNotificacoes ? 'ri-notification-3-line' : 'ri-notification-off-line'}
                              label={minhasNotificacoes ? `Desativar minha notificação de ${o.categoriaNome}` : `Ativar minha notificação de ${o.categoriaNome}`}
                              tone={minhasNotificacoes ? 'primary' : 'muted'}
                              disabled={alternarMinhaNotificacao.isPending || !meuParticipante}
                              onClick={() => meuParticipante && alternarMinhaNotificacao.mutate({ participanteId: meuParticipante.id, ativas: !minhasNotificacoes })}
                            />
                            <IconButton icon="ri-logout-box-line" label={`Sair do orçamento conjunto de ${o.categoriaNome}`} tone="danger" onClick={() => setParaSair(o)} />
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </CardBody>
            )}
          </Card>
        </div>
      )}

      {lancamentosDe && <LancamentosOrcamentoModal orcamento={lancamentosDe} onClose={() => setLancamentosDe(null)} onEditar={() => { const o = lancamentosDe; setLancamentosDe(null); openModal(o) }} />}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Definir orçamento"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button icon="ri-save-line" onClick={submit} loading={salvar.isPending}>Salvar</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select label="Categoria" value={form.categoriaId} onChange={(e) => setForm((f) => ({ ...f, categoriaId: e.target.value }))}>
            <option value="">Selecione...</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
          </Select>
          {editando && editando.souDono && form.categoriaId && form.categoriaId !== editando.categoriaId && (
            <p className="text-2xs text-warning">Troca a categoria deste mês e dos meses seguintes; os anteriores continuam como estavam.{editando.acumularSaldo ? ' O saldo acumulado recomeça na nova categoria a partir deste mês.' : ''}</p>
          )}
          {!form.modoAlvo && (
            <Input label="Limite mensal (R$)" type="number" min={0} step="0.01" value={form.limiteValor} onChange={(e) => setForm((f) => ({ ...f, limiteValor: e.target.value }))} placeholder="0,00" />
          )}
          {!form.modoAlvo && form.categoriaId && (
            <div className="space-y-2">
              <Button size="sm" variant="light" icon="ri-magic-line" loading={sugerir.isPending} onClick={() => sugerir.mutate()}>Sugerir limite pelo gasto real</Button>
              {sugestao && <PainelSugestao s={sugestao} onUsar={(v) => setForm((f) => ({ ...f, limiteValor: String(v) }))} />}
            </div>
          )}
          {temPlano && (
            <div className="space-y-3 rounded bg-surface2 p-3">
              <div className="space-y-2">
                <span className="form-label">Tipo de meta<Dica texto="Define como o orçamento é avaliado: gastar no máximo, gastar um valor certo, ou chegar a um mínimo." /></span>
                <div className="grid grid-cols-3 gap-2">
                  {TIPOS_META.map((t) => (
                    <button
                      key={t.valor} type="button" aria-pressed={form.tipoMeta === t.valor} title={t.dica}
                      onClick={() => setForm((f) => ({ ...f, tipoMeta: t.valor, essencial: t.valor === 2 && f.tipoMeta !== 2 ? true : t.valor !== 2 && f.tipoMeta === 2 ? false : f.essencial }))}
                      className={`h-[38px] rounded border text-13 transition-colors ${form.tipoMeta === t.valor ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-2xs text-muted">{TIPOS_META.find((t) => t.valor === form.tipoMeta)?.dica}</p>
              </div>
              <div className="space-y-2">
                <span className="form-label">Projetar como<Dica texto="Como o que falta gastar neste orçamento entra no caixa e nos próximos meses." /></span>
                <div className="grid grid-cols-3 gap-2">
                  {PROJETAR_COMO.map((t) => (
                    <button
                      key={t.valor} type="button" aria-pressed={form.projetarComo === t.valor} title={t.dica}
                      onClick={() => setForm((f) => ({ ...f, projetarComo: t.valor }))}
                      className={`h-[38px] rounded border text-13 transition-colors ${form.projetarComo === t.valor ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-2xs text-muted">{PROJETAR_COMO.find((t) => t.valor === form.projetarComo)?.dica}</p>
              </div>
              <Toggle label="Essencial (conta que não dá para deixar de pagar)" checked={form.essencial} onChange={(v) => setForm((f) => ({ ...f, essencial: v }))} />
              {form.tipoMeta === 1 && (
                <>
                  <Toggle label="Controlar por ritmo (semana, dia...)" checked={form.ritmoAtivo} onChange={(v) => setForm((f) => ({ ...f, ritmoAtivo: v }))} />
                  {form.ritmoAtivo && (
                    <div className="space-y-3">
                      <Select label="Cadência" value={String(form.ritmoCadencia)} onChange={(e) => setForm((f) => ({ ...f, ritmoCadencia: Number(e.target.value) }))}>
                        {CADENCIAS.map((c) => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                      </Select>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {form.ritmoCadencia === 3 && (
                          <Input label="A cada quantos dias" type="number" min={1} step={1} value={form.ritmoDias} onChange={(e) => setForm((f) => ({ ...f, ritmoDias: e.target.value }))} />
                        )}
                        <Input label="Pode gastar por período (R$)" type="number" min={0} step="0.01" value={form.ritmoValor} onChange={(e) => setForm((f) => ({ ...f, ritmoValor: e.target.value }))} placeholder="0,00" />
                      </div>
                      <Toggle label="A sobra de cada período soma no próximo" checked={form.ritmoAcumula} onChange={(v) => setForm((f) => ({ ...f, ritmoAcumula: v }))} />
                      <p className="text-2xs text-muted">Os períodos contam a partir do dia 1 do mês. Estourar um período não vira dívida no seguinte.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="rounded bg-surface2 p-3">
            <Toggle label="Alertar ao chegar em 80% do limite" checked={form.alertaAtivo} onChange={(v) => setForm((f) => ({ ...f, alertaAtivo: v }))} />
          </div>
          <div className="space-y-3 rounded bg-surface2 p-3">
            <Toggle
              label="Acumular saldo não usado (reserva)"
              checked={form.acumularSaldo || form.modoAlvo}
              disabled={form.modoAlvo}
              onChange={(v) => setForm((f) => ({ ...f, acumularSaldo: v }))}
            />
            <p className="text-2xs text-muted">
              O que sobrar neste mês soma no limite do mês seguinte, em vez de resetar — útil pra contas periódicas (seguro, IPVA) ou reserva de emergência.
            </p>
            {(form.acumularSaldo || form.modoAlvo) && (
              <label className="flex items-center gap-2 text-13 text-white">
                <input type="checkbox" checked={form.modoAlvo} onChange={(e) => setForm((f) => ({ ...f, modoAlvo: e.target.checked }))} className="h-4 w-4 rounded accent-primary" />
                Definir valor alvo e data (calcula o limite mensal sozinho)
              </label>
            )}
            {form.modoAlvo && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Valor alvo (R$)" type="number" min={0} step="0.01" value={form.valorAlvo} onChange={(e) => setForm((f) => ({ ...f, valorAlvo: e.target.value }))} placeholder="0,00" />
                <Input label="Até quando" type="date" value={form.dataAlvo} onChange={(e) => setForm((f) => ({ ...f, dataAlvo: e.target.value }))} />
              </div>
            )}
            {editando && (editando.acumularSaldo || form.modoAlvo) && editando.saldoAcumulado > 0 && (
              <p className="text-2xs text-income">Já acumulado: {formatMoney(editando.saldoAcumulado)}</p>
            )}
          </div>
          <div className="space-y-2">
            <span className="form-label">Repetição</span>
            <div className="grid grid-cols-3 gap-2">
              {([
                { valor: 'mensal' as const, label: 'Mensalmente' },
                { valor: 'intervalo' as const, label: 'A cada X meses' },
                { valor: 'unico' as const, label: 'Só este mês' },
              ]).map((op) => (
                <button
                  key={op.valor} type="button" aria-pressed={form.repeticao === op.valor}
                  onClick={() => setForm((f) => ({ ...f, repeticao: op.valor }))}
                  className={`h-[38px] rounded border text-13 transition-colors ${form.repeticao === op.valor ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
                >
                  {op.label}
                </button>
              ))}
            </div>
            {form.repeticao === 'intervalo' && (
              <Input label="A cada quantos meses" type="number" min={2} max={24} step={1} value={form.intervaloMeses} onChange={(e) => setForm((f) => ({ ...f, intervaloMeses: e.target.value }))} />
            )}
            <p className="text-2xs text-muted">
              {form.repeticao === 'unico'
                ? 'Vale só para este mês — os seguintes ficam sem orçamento até você criar de novo.'
                : 'O orçamento do mês seguinte é criado sozinho (mesmo limite e alerta) na primeira vez que você abrir aquele mês.'}
            </p>
          </div>
          {editando && editando.repetirACadaMeses != null && form.repeticao !== 'unico' && (
            <div className="space-y-2">
              <span className="form-label">Esse novo valor vale para...</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button" aria-pressed={!form.somenteEsteMes}
                  onClick={() => setForm((f) => ({ ...f, somenteEsteMes: false }))}
                  className={`h-[38px] rounded border text-13 transition-colors ${!form.somenteEsteMes ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
                >
                  Este e os próximos
                </button>
                <button
                  type="button" aria-pressed={form.somenteEsteMes}
                  onClick={() => setForm((f) => ({ ...f, somenteEsteMes: true }))}
                  className={`h-[38px] rounded border text-13 transition-colors ${form.somenteEsteMes ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
                >
                  Só este mês
                </button>
              </div>
              <p className="text-2xs text-muted">
                {form.somenteEsteMes
                  ? 'Ajuste pontual — os meses seguintes continuam vindo do valor anterior.'
                  : 'Vira o novo valor padrão a partir deste mês.'}
              </p>
            </div>
          )}
          <div className="rounded bg-surface2 p-3">
            <Toggle label="Orçamento conjunto — soma o gasto de outra pessoa" checked={form.conjunto} onChange={(v) => setForm((f) => ({ ...f, conjunto: v }))} />
          </div>
          {form.conjunto && (
            <div className="space-y-2">
              <p className="text-2xs text-muted">
                Convide quem já compartilha conta com você. Cada pessoa escolhe a própria categoria ao aceitar — o gasto dela só entra depois disso.
              </p>
              {pessoas.length === 0 ? (
                <p className="rounded bg-surface2 p-3 text-13 text-muted">Você ainda não tem ninguém compartilhado pra convidar.</p>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-border/60 p-2">
                  {pessoas.map((p) => (
                    <label key={p.userId} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-surface2">
                      <input type="checkbox" checked={form.participantesUserIds.includes(p.userId)} onChange={() => togglePessoa(p.userId)} className="h-4 w-4 rounded accent-primary" />
                      <span className="text-13 text-white">{p.nome}</span>
                      <span className="text-2xs text-muted">{p.email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-2xs text-muted">Se a categoria já tiver orçamento neste mês, o limite é atualizado.</p>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!convite}
        onClose={() => setConvite(null)}
        title="Aceitar convite"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setConvite(null)}>Cancelar</Button>
            <Button icon="ri-check-line" loading={aceitarConvite.isPending} disabled={!categoriaConvite} onClick={() => aceitarConvite.mutate()}>Aceitar</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-13 text-muted">Qual categoria sua entra nesse orçamento conjunto?</p>
          <Select label="Sua categoria" value={categoriaConvite} onChange={(e) => setCategoriaConvite(e.target.value)}>
            <option value="">Selecione...</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
          </Select>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        loading={remover.isPending}
        confirmLabel="Sim, remover"
        message={<>O orçamento de <strong className="text-white">{paraExcluir?.categoriaNome}</strong> deste mês será removido.</>}
      />

      <ConfirmDialog
        open={!!paraSair}
        onClose={() => setParaSair(null)}
        onConfirm={() => {
          const meuParticipante = paraSair?.participantes?.find((p) => p.souEu)
          if (meuParticipante) sair.mutate(meuParticipante.id)
        }}
        loading={sair.isPending}
        confirmLabel="Sim, sair"
        message={<>Você sai do orçamento conjunto de <strong className="text-white">{paraSair?.categoriaNome}</strong> — seu gasto para de entrar na soma.</>}
      />
    </>
  )
}

function mesesAtras(n: number) {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return d.toISOString().slice(0, 10)
}

function HistoricoOrcamentoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [de, setDe] = useState(mesesAtras(5))
  const [ate, setAte] = useState(new Date().toISOString().slice(0, 10))

  const { data, isLoading } = useQuery({
    queryKey: ['orcamento-historico', de, ate],
    queryFn: () => orcamentosApi.historico(de, ate),
    enabled: open,
  })
  const itens = data?.itens ?? []

  const porCategoria = new Map<string, typeof itens>()
  for (const it of itens) {
    const lista = porCategoria.get(it.categoriaNome) ?? []
    lista.push(it)
    porCategoria.set(it.categoriaNome, lista)
  }

  return (
    <Modal open={open} onClose={onClose} title="Histórico de orçamento" size="lg">
      <div className="mb-4 flex flex-wrap items-end gap-3 border-b border-border/60 pb-4">
        <Input label="De" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
        <Input label="Até" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
        {data && (
          <p className="mb-2.5 text-13 text-muted">
            No período: <span className="font-medium text-white">{formatMoney(data.totalGastoPeriodo)}</span> gastos de {formatMoney(data.totalOrcadoPeriodo)} orçados
          </p>
        )}
      </div>
      {isLoading ? <LoadingState /> : itens.length === 0 ? (
        <EmptyState icon="ri-line-chart-line" message="Sem orçamentos definidos neste período." />
      ) : (
        <div className="space-y-6">
          {[...porCategoria.entries()].map(([nome, meses]) => (
            <div key={nome}>
              <h5 className="mb-2 text-13 font-medium text-white">{nome}</h5>
              <div className="space-y-1.5">
                {meses.map((m) => {
                  const pct = m.limiteValor > 0 ? Math.min((m.gastoAtual / m.limiteValor) * 100, 100) : 0
                  return (
                    <div key={`${m.mes}-${m.ano}`} className="flex items-center gap-3 text-13">
                      <span className="w-16 flex-shrink-0 text-muted">{m.label}</span>
                      <ProgressBar value={pct} tone={m.estourou ? 'expense' : 'income'} className="flex-1" />
                      <span className={`w-40 flex-shrink-0 text-right ${m.estourou ? 'text-expense' : 'text-muted'}`}>
                        {formatMoney(m.gastoAtual)} / {formatMoney(m.limiteValor)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
